/**
 * Distribution date field.
 *
 * The artwork shows only the closed field (gap G-03). The open state is a
 * month calendar in a modal — the previous horizontal strip of days made
 * picking a date three weeks out a long swipe with no sense of position.
 *
 * The window itself is the server's: `minDaysAhead` and `maxDaysAhead` come
 * from `/config`, and the server re-validates on submit regardless. This is
 * fast feedback, not the rule.
 */

import { memo, useMemo, useState } from 'react';
import { Pressable, StyleSheet, View } from 'react-native';

import { formatDate } from '@mfa/domain';
import { colors, radius, space } from '../../theme';
import { CalendarModal } from './CalendarModal';
import { Glyph } from '../common/Icon';
import { Text } from '../common/Text';
import { useT } from '../../core/i18n/LanguageProvider';

export interface DatePickerFieldProps {
  value: string | null;
  onChange: (isoDate: string) => void;
  /** The server's own today, so the window cannot drift from its validator. */
  today?: string;
  minDaysAhead: number;
  maxDaysAhead: number;
  blackoutDates: string[];
  error?: string | null;
  testID?: string;
}

const DAY_MS = 86_400_000;

/** `2026-09-12` as local midnight, not the UTC instant `new Date(s)` gives. */
function parseISODate(iso: string): Date {
  const [year, month, day] = iso.split('-').map(Number);
  if (!year || !month || !day) return new Date();
  return new Date(year, month - 1, day);
}

export const DatePickerField = memo(function DatePickerField({
  value,
  onChange,
  today,
  minDaysAhead,
  maxDaysAhead,
  blackoutDates,
  error,
  testID = 'date-picker',
}: DatePickerFieldProps) {
  const t = useT();
  const [open, setOpen] = useState(false);

  const { minDate, maxDate } = useMemo(() => {
    // Counted from the server's date when it has told us one. Counting from
    // the device's meant a donor west of UTC was offered, in their evening, a
    // date the server then refused as "at least 1 day from today" — the
    // calendar and the validator were reading different clocks.
    const start = today ? parseISODate(today) : new Date();
    start.setHours(0, 0, 0, 0);
    return {
      minDate: new Date(start.getTime() + minDaysAhead * DAY_MS),
      maxDate: new Date(start.getTime() + maxDaysAhead * DAY_MS),
    };
  }, [today, minDaysAhead, maxDaysAhead]);

  return (
    <View testID={testID}>
      <Pressable
        testID={`${testID}-trigger`}
        onPress={() => setOpen((current) => !current)}
        accessibilityRole="button"
        accessibilityLabel={
          value ? `Distribution date, ${formatDate(value)}. Tap to change.` : 'Choose a distribution date'
        }
        accessibilityState={{ expanded: open }}
        style={[styles.trigger, error ? styles.triggerError : null]}
      >
        <Glyph name="calendar" size={18} />
        <Text variant="body" tone={value ? 'primary' : 'disabled'} style={styles.triggerText}>
          {value ? formatDate(value) : t('Select a date')}
        </Text>
        <Text variant="caption" tone="brand">
          {open ? t('Close') : t('Change')}
        </Text>
      </Pressable>

      <CalendarModal
        visible={open}
        value={value}
        onCancel={() => setOpen(false)}
        onSelect={(iso) => {
          onChange(iso);
          setOpen(false);
        }}
        minDate={minDate}
        maxDate={maxDate}
        blackoutDates={blackoutDates}
        testID={`${testID}-calendar`}
      />

      {error ? (
        <Text
          variant="caption"
          tone="danger"
          accessibilityLiveRegion="polite"
          style={styles.error}
          testID={`${testID}-error`}
        >
          {error}
        </Text>
      ) : null}
    </View>
  );
});

const styles = StyleSheet.create({
  trigger: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: space.sm,
    borderWidth: 1,
    borderColor: colors.border.default,
    borderRadius: radius.md,
    paddingHorizontal: space.md,
    paddingVertical: space.md,
    minHeight: 52,
  },
  triggerError: {
    borderColor: colors.text.danger,
  },
  triggerText: {
    flex: 1,
  },
  strip: {
    gap: space.sm,
    paddingVertical: space.md,
  },
  day: {
    width: 64,
    paddingVertical: space.md,
    borderRadius: radius.md,
    borderWidth: 1,
    borderColor: colors.border.default,
    alignItems: 'center',
    gap: space.xxs,
    backgroundColor: colors.bg.surface,
  },
  daySelected: {
    backgroundColor: colors.bg.brand,
    borderColor: colors.bg.brand,
  },
  dayDisabled: {
    backgroundColor: colors.bg.surfaceSunken,
    opacity: 0.6,
  },
  error: {
    marginTop: space.sm,
  },
});
